/**
 * Mod-only "Search InterPals photos" reverse-image modal.
 *
 * Triggered from the photo lightbox menu ("Search InterPals photos").
 * Fetches GET /v1/mod/photos/{id}/reuse via ApiClient and lists the
 * other photos on the platform whose perceptual hash matches the
 * current image, with the owning account, account status, and a link to
 * the profile.
 *
 * Result rows are built imperatively with DOM APIs and all
 * server-supplied text (usernames) is written through `textContent` —
 * never `innerHTML` — so a crafted username can't inject markup (see
 * backend/CLAUDE.md "Don't use x-html for AJAX modal/panel content").
 */
(function () {
    'use strict';

    var modalEl = null;
    var historicalSeq = 0;
    var state = {
        photoId: '',
        requestSeq: 0,
        priorBodyOverflow: null,
    };

    function ensureModal() {
        if (modalEl) return modalEl;
        modalEl = document.createElement('div');
        modalEl.id = 'photo-reuse-lightbox-modal';
        modalEl.className = 'hidden fixed inset-0 z-[10010]';
        // Static chrome only — never interpolates server data, so innerHTML
        // here is safe. The result rows are built separately via DOM APIs.
        modalEl.innerHTML = ''
            + '<div class="photo-reuse-backdrop fixed inset-0 bg-black/50 flex items-end justify-center sm:items-center sm:p-6">'
            + '  <div class="photo-reuse-card bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[90dvh] overflow-hidden shadow-2xl">'
            + '    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">'
            + '      <div>'
            + '        <h2 class="m-0 text-sm font-semibold text-gray-900 dark:text-gray-100">Search InterPals photos</h2>'
            + '        <p class="m-0 mt-0.5 text-xs text-gray-500 dark:text-gray-400">Other accounts whose photo matches this image.</p>'
            + '      </div>'
            + '      <button type="button" class="photo-reuse-close p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 text-lg leading-none border-0 bg-transparent cursor-pointer" aria-label="Close">&times;</button>'
            + '    </div>'
            + '    <div class="photo-reuse-body flex-1 overflow-y-auto p-3"></div>'
            + '  </div>'
            + '</div>';

        document.body.appendChild(modalEl);

        var backdrop = modalEl.querySelector('.photo-reuse-backdrop');
        var card = modalEl.querySelector('.photo-reuse-card');
        var closeBtn = modalEl.querySelector('.photo-reuse-close');

        backdrop.addEventListener('click', function (e) {
            if (e.target === backdrop) close();
        });
        card.addEventListener('click', function (e) { e.stopPropagation(); });
        closeBtn.addEventListener('click', close);
        document.addEventListener('keydown', function (e) {
            if (!modalEl.classList.contains('hidden') && e.key === 'Escape') close();
        });

        return modalEl;
    }

    function bodyHost() {
        return modalEl.querySelector('.photo-reuse-body');
    }

    function clearHost() {
        var host = bodyHost();
        while (host.firstChild) host.removeChild(host.firstChild);
        return host;
    }

    function renderMessage(text) {
        var host = clearHost();
        var p = document.createElement('p');
        p.className = 'm-0 px-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400';
        p.textContent = text;
        host.appendChild(p);
    }

    function renderLoading() {
        var host = clearHost();
        var p = document.createElement('p');
        p.className = 'm-0 px-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400';
        p.textContent = 'Searching…';
        host.appendChild(p);
    }

    function statusBadge(status) {
        var span = document.createElement('span');
        var base = 'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ';
        var tone;
        if (status === 'active') {
            tone = 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
        } else if (status === 'frozen') {
            tone = 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
        } else {
            tone = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
        }
        span.className = base + tone;
        span.textContent = status || 'unknown';
        return span;
    }

    function distanceLabel(distance) {
        var span = document.createElement('span');
        span.className = 'text-[11px] text-gray-400 dark:text-gray-500';
        span.textContent = distance === 0 ? 'exact match' : 'near match (Δ ' + distance + ')';
        return span;
    }

    function buildRow(match) {
        var row = document.createElement('div');
        row.className = 'flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60';

        // Thumbnail
        var thumbWrap = document.createElement('div');
        thumbWrap.className = 'w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0';
        if (match.thumb_url) {
            var img = document.createElement('img');
            img.src = match.thumb_url;
            img.alt = '';
            img.loading = 'lazy';
            img.className = 'w-full h-full object-cover';
            thumbWrap.appendChild(img);
        }
        row.appendChild(thumbWrap);

        // Identity + meta
        var meta = document.createElement('div');
        meta.className = 'flex-1 min-w-0';

        var topLine = document.createElement('div');
        topLine.className = 'flex items-center gap-2 min-w-0';

        var nameEl;
        var displayName = match.username || ('user ' + match.user_id);
        // Only treat profile_url as a link when it's a same-origin relative
        // path (LinksHelper::getProfile always emits "/username"). Guards the
        // href against any future change that could let a non-relative or
        // scheme-bearing value through.
        if (match.profile_url && String(match.profile_url).charAt(0) === '/'
            && String(match.profile_url).charAt(1) !== '/') {
            nameEl = document.createElement('a');
            nameEl.href = match.profile_url;
            nameEl.target = '_blank';
            nameEl.rel = 'noopener';
            nameEl.className = 'text-sm font-medium text-sky-600 dark:text-sky-400 hover:underline truncate';
        } else {
            nameEl = document.createElement('span');
            nameEl.className = 'text-sm font-medium text-gray-700 dark:text-gray-200 truncate';
        }
        nameEl.textContent = displayName;
        topLine.appendChild(nameEl);
        topLine.appendChild(statusBadge(match.account_status));
        meta.appendChild(topLine);

        meta.appendChild(distanceLabel(match.hamming_distance));
        row.appendChild(meta);

        return row;
    }

    // Maps an ai_moderation_shadow_log final_outcome to a labeled badge,
    // mirroring the photo-ban history page (mod/photo_bans/history.twig).
    function outcomeBadge(finalOutcome) {
        var span = document.createElement('span');
        var base = 'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ';
        var label;
        var tone;
        if (finalOutcome == null) {
            label = 'Pending';
            tone = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
        } else if (finalOutcome === 'mod_deleted') {
            label = 'Deleted by mod';
            tone = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
        } else if (finalOutcome === 'mod_approved') {
            label = 'Approved';
            tone = 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
        } else if (String(finalOutcome).indexOf('llm_') === 0) {
            label = 'LLM: ' + String(finalOutcome).slice(4);
            tone = 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
        } else {
            label = String(finalOutcome).replace(/_/g, ' ');
            tone = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
        }
        span.className = base + tone;
        span.textContent = label;
        return span;
    }

    function buildHistoricalRow(match) {
        var row = document.createElement('div');
        row.className = 'flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60';

        // Last-known avatar (the live photo is gone for deleted accounts),
        // falling back to an empty tile like the live rows.
        var thumbWrap = document.createElement('div');
        thumbWrap.className = 'w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0';
        if (match.avatar_url) {
            var img = document.createElement('img');
            img.src = match.avatar_url;
            img.alt = '';
            img.loading = 'lazy';
            img.className = 'w-full h-full object-cover';
            thumbWrap.appendChild(img);
        }
        row.appendChild(thumbWrap);

        var meta = document.createElement('div');
        meta.className = 'flex-1 min-w-0';

        var topLine = document.createElement('div');
        topLine.className = 'flex items-center gap-2 min-w-0';

        var displayName = match.username || ('user ' + match.user_id);
        var nameEl;
        // Same-origin relative path guard as the live rows.
        if (match.mod_page_url && String(match.mod_page_url).charAt(0) === '/'
            && String(match.mod_page_url).charAt(1) !== '/') {
            nameEl = document.createElement('a');
            nameEl.href = match.mod_page_url;
            nameEl.target = '_blank';
            nameEl.rel = 'noopener';
            nameEl.className = 'text-sm font-medium text-sky-600 dark:text-sky-400 hover:underline truncate'
                + (match.is_deleted ? ' line-through' : '');
        } else {
            nameEl = document.createElement('span');
            nameEl.className = 'text-sm font-medium text-gray-700 dark:text-gray-200 truncate'
                + (match.is_deleted ? ' line-through' : '');
        }
        nameEl.textContent = displayName;
        topLine.appendChild(nameEl);

        if (match.is_deleted) {
            var del = document.createElement('span');
            del.className = 'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
            del.textContent = 'deleted';
            topLine.appendChild(del);
        }
        meta.appendChild(topLine);

        var sub = document.createElement('div');
        sub.className = 'flex flex-wrap items-center gap-2 mt-0.5';
        if (match.hamming_distance != null) {
            sub.appendChild(distanceLabel(match.hamming_distance));
        }
        sub.appendChild(outcomeBadge(match.final_outcome));
        meta.appendChild(sub);

        row.appendChild(meta);
        return row;
    }

    // Collapsible "Show N deleted accounts" section for accounts the
    // photo-hash gate already caught and removed. Starts collapsed.
    function buildHistoricalSection(historical, banHistoryUrl, truncated) {
        var wrap = document.createElement('div');
        wrap.className = 'mt-2 border-t border-gray-100 dark:border-gray-800 pt-2';

        var allDeleted = historical.every(function (h) { return h.is_deleted; });
        var n = historical.length;
        var noun = allDeleted
            ? (n === 1 ? 'deleted account' : 'deleted accounts')
            : (n === 1 ? 'matched account' : 'matched accounts');
        var collapsedLabel = 'Show ' + n + ' ' + noun;
        var expandedLabel = 'Hide ' + n + ' ' + noun;

        var toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'flex w-full items-center justify-between gap-2 px-2 py-2 min-h-[44px] rounded-lg text-left text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/60 border-0 bg-transparent cursor-pointer';
        toggle.setAttribute('aria-expanded', 'false');

        var labelWrap = document.createElement('span');
        labelWrap.className = 'flex items-center gap-2 min-w-0';
        var countPill = document.createElement('span');
        countPill.className = 'inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 text-[11px] font-semibold';
        countPill.textContent = String(n);
        // Decorative — the count is already in the label text below.
        countPill.setAttribute('aria-hidden', 'true');
        labelWrap.appendChild(countPill);
        var labelText = document.createElement('span');
        labelText.className = 'truncate';
        labelText.textContent = collapsedLabel;
        labelWrap.appendChild(labelText);
        toggle.appendChild(labelWrap);

        var chevron = document.createElement('span');
        chevron.className = 'flex-none text-gray-400 dark:text-gray-500 text-xs';
        chevron.textContent = '▾';
        chevron.setAttribute('aria-hidden', 'true');
        toggle.appendChild(chevron);

        var body = document.createElement('div');
        body.className = 'hidden';
        // Unique id so aria-controls stays valid even if the section is ever
        // rebuilt within the same document.
        body.id = 'photo-reuse-historical-body-' + (++historicalSeq);
        toggle.setAttribute('aria-controls', body.id);

        var caption = document.createElement('p');
        caption.className = 'm-0 px-2 pt-1 pb-2 text-[11px] text-gray-400 dark:text-gray-500';
        caption.textContent = 'Caught by the photo-ban gate and removed, so they no longer appear in the live search above.'
            + (truncated ? ' Showing the first ' + n + '. More may exist.' : '');
        body.appendChild(caption);

        var list = document.createElement('div');
        list.className = 'flex flex-col';
        historical.forEach(function (h) { list.appendChild(buildHistoricalRow(h)); });
        body.appendChild(list);

        if (banHistoryUrl && String(banHistoryUrl).charAt(0) === '/'
            && String(banHistoryUrl).charAt(1) !== '/') {
            var footer = document.createElement('a');
            footer.href = banHistoryUrl;
            footer.target = '_blank';
            footer.rel = 'noopener';
            footer.className = 'flex items-center min-h-[44px] px-2 text-xs font-medium text-sky-600 dark:text-sky-400 hover:underline';
            footer.textContent = 'View full ban history →';
            body.appendChild(footer);
        }

        toggle.addEventListener('click', function () {
            var expanded = body.classList.toggle('hidden') === false;
            toggle.setAttribute('aria-expanded', String(expanded));
            chevron.textContent = expanded ? '▴' : '▾';
            labelText.textContent = expanded ? expandedLabel : collapsedLabel;
        });

        wrap.appendChild(toggle);
        wrap.appendChild(body);
        return wrap;
    }

    function renderResults(data) {
        if (!data || data.status === 'no_hash') {
            renderMessage('This photo has no perceptual hash yet, so it can\'t be searched.');
            return;
        }

        var matches = data.matches || [];
        var historical = data.historical_matches || [];

        if (!matches.length && !historical.length) {
            renderMessage('No other InterPals photos match this image.');
            return;
        }

        var host = clearHost();

        if (matches.length) {
            var summary = document.createElement('p');
            summary.className = 'm-0 px-2 pb-2 text-xs text-gray-500 dark:text-gray-400';
            var count = data.match_count != null ? data.match_count : matches.length;
            if (data.truncated) {
                summary.textContent = 'Showing the first ' + matches.length + ' matches. More may exist.';
            } else {
                summary.textContent = count + (count === 1 ? ' other photo matches.' : ' other photos match.');
            }
            host.appendChild(summary);

            var list = document.createElement('div');
            list.className = 'flex flex-col';
            matches.forEach(function (m) { list.appendChild(buildRow(m)); });
            host.appendChild(list);
        } else {
            // No live copies, but the gate previously caught (and removed)
            // accounts using this image. Explain the empty live search.
            var note = document.createElement('p');
            note.className = 'm-0 px-2 pt-4 pb-1 text-center text-sm text-gray-500 dark:text-gray-400';
            note.textContent = 'No live accounts currently use this image.';
            host.appendChild(note);
        }

        if (historical.length) {
            host.appendChild(buildHistoricalSection(
                historical,
                data.historical_ban_history_url,
                data.historical_truncated
            ));
        }
    }

    function fetchAndRender() {
        renderLoading();

        if (!window.ApiClient) {
            renderMessage('ApiClient unavailable.');
            return;
        }

        var seq = ++state.requestSeq;
        var photoId = state.photoId;

        window.ApiClient.request(
            '/mod/photos/' + encodeURIComponent(photoId) + '/reuse',
            { method: 'GET' }
        ).then(function (res) {
            if (res && typeof res.json === 'function') {
                return res.json().then(function (body) {
                    return { ok: res.ok, status: res.status, body: body };
                });
            }
            return { ok: true, status: 200, body: res };
        }).then(function (envelope) {
            // Drop a stale response if the modal was closed or reopened for
            // another photo while this request was in flight.
            if (seq !== state.requestSeq || modalEl.classList.contains('hidden')) {
                return;
            }
            if (envelope.ok) {
                renderResults(envelope.body);
                return;
            }
            renderMessage(
                (envelope.body && envelope.body.error) ? envelope.body.error : 'Search failed.'
            );
        }).catch(function (err) {
            if (seq !== state.requestSeq || modalEl.classList.contains('hidden')) {
                return;
            }
            renderMessage((err && err.message) ? err.message : 'Network error.');
        });
    }

    function open(photoId) {
        ensureModal();
        state.photoId = String(photoId || '');
        modalEl.classList.remove('hidden');
        if (typeof document !== 'undefined' && document.body) {
            state.priorBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        fetchAndRender();
    }

    function close() {
        if (!modalEl) return;
        modalEl.classList.add('hidden');
        // Invalidate any in-flight request so its late response is ignored.
        state.requestSeq++;
        state.photoId = '';
        if (typeof document !== 'undefined' && document.body) {
            document.body.style.overflow = state.priorBodyOverflow || '';
            state.priorBodyOverflow = null;
        }
    }

    window.openPhotoReuseModal = open;
})();
